import { Box, Icon, Theme, Typography, useTheme } from "@outschool/backpack";
import { faCalendar } from "@outschool/icons";
import { useLocale } from "@outschool/localization";
import { activityPath } from "@outschool/routes";
import { UserHeadshotImage } from "@outschool/ui-components-shared";
import { useIsMobile, useLinkComponent } from "@outschool/ui-utils";
import React from "react";

import usePriceDetailsString from "../../hooks/usePriceDetailsString";
import { LeaderDetailsListing } from "../../Listings";
import { SimpleRatings } from "../../Reviews";
import { ClassDetails } from "./ClassDetails";
import { DefaultImage } from "./DefaultImage";

export type ActivityCardContentType = {
  uid: string;
  title: string;
  isClub: boolean;
  isSelfPaced?: boolean;
  leader: {
    __typename?: "Leader" | undefined;
    uid: string;
    name: string | null;
    photo: string | null;
    averageActivityStarRating?: number | null;
    reviewCount?: number;
    leader_link?: string | null;
    timeZone?: string | null;
  };
  details: {
    photo: string | null;
  };
};

export type SectionCardContentType = {
  uid: string;
  leader: {
    __typename?: "Leader" | undefined;
    uid: string;
    name: string | null;
    photo: string | null;
    averageActivityStarRating: number | null;
    reviewCount: number;
  };
};

interface SectionCardContentProps {
  activity: ActivityCardContentType;
  section?: SectionCardContentType;
  price: string;
  formattedCadenceString: string;
  formattedDurationString: string;
  mobile?: boolean;
}

export default function SectionCardContent({
  activity,
  section,
  price,
  formattedCadenceString,
  formattedDurationString,
  mobile = false,
}: SectionCardContentProps) {
  const Link = useLinkComponent();
  const isMobile = useIsMobile() || mobile;
  const locale = useLocale();
  const theme = useTheme();
  const { perClassString } = usePriceDetailsString();

  // TODO: remove this any once we validate all callers.
  const sectionLeader: any = section?.leader;
  const activityLeader: any = activity?.leader;
  // Favor section leaders in the event it is a seller org.
  const leader = sectionLeader || activityLeader;

  return (
    <Box
      component={Link}
      target={"_blank"}
      to={activityPath(activity, true, locale)}
      flex
      sx={{
        flexDirection: "column",
        borderRadius: 16,
        position: "relative",
        backgroundColor: "common.white",
        width: "100%",
        cursor: "pointer",
        textDecoration: "none !important",
        "&:hover": {
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.25)",
        },
        boxShadow: "0px 8px 16px 0px #00000014",
      }}
    >
      {!isMobile && (
        <DefaultImage
          imageSx={{ height: "132px", borderRadius: "12px 12px 0 0" }}
          imageUrl={activity.details.photo || undefined}
        />
      )}
      <ClassDetails>
        <Box flex sx={{ gap: "8px" }}>
          {isMobile && (
            <DefaultImage
              imageSx={{ height: "48px", width: "48px", borderRadius: "8px" }}
              imageUrl={activity.details.photo || undefined}
            />
          )}
          <Typography
            variant="subtitle2"
            sx={(theme: Theme) => ({
              color: theme.palette.grey[900],
              display: "-webkit-box",
              WebkitLineClamp: "3",
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              minHeight: 54,
              [theme.breakpoints.down("md")]: {
                marginLeft: "4px",
              },
            })}
          >
            {activity.title}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            height: "49px",
            color: "black",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box flex sx={{ gap: "8px" }}>
            {!isMobile && (
              <UserHeadshotImage
                user={leader}
                size={32}
                sx={(theme: Theme) => ({
                  marginRight: "6px",
                  [theme.breakpoints.down("md")]: {
                    marginRight: "6px",
                  },
                })}
              />
            )}
            {isMobile ? (
              <LeaderDetailsListing leader={leader} />
            ) : (
              <Box
                sx={(theme: Theme) => ({
                  [theme.breakpoints.down("md")]: {
                    marginLeft: "4px",
                  },
                })}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.grey[600],
                    height: "24px",
                    width: "116px",
                    display: "-webkit-box",
                    WebkitLineClamp: "1",
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {leader?.name}
                </Typography>
                {activityLeader?.averageActivityStarRating && (
                  <SimpleRatings
                    isBolded={false}
                    starRating={activityLeader?.averageActivityStarRating}
                    // eslint-disable-next-line i18next/no-literal-string
                    reviewCount={activityLeader?.reviewCount || null}
                  />
                )}
              </Box>
            )}
          </Box>
          <Box
            sx={{
              textAlign: "right",
              minWidth: "53px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant={isMobile ? "body2" : "h5"}
              emphasized
              sx={{ color: isMobile && theme.palette.grey[900] }}
            >
              {`${price}`}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.grey[600] }}
            >
              {perClassString(activity as any)}
            </Typography>
          </Box>
        </Box>
      </ClassDetails>
      <Box
        sx={{
          display: "flex",
          height: "32px",
          backgroundColor: theme.palette.grey[50],
          justifyContent: "space-between",
          alignItems: "center",
          padding: "4px 16px 4px 16px",
          color: theme.palette.grey[600],
          borderRadius: "0 0 16px 16px",
        }}
      >
        <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <Icon icon={faCalendar} sx={{ fontSize: "12px" }} />
          <Typography variant="caption">{formattedCadenceString}</Typography>
        </Box>
        <Typography variant="caption">{formattedDurationString}</Typography>
      </Box>
    </Box>
  );
}
